import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Camera, Zap, ZapOff, Check, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface MultiPhotoCameraCaptureProps {
  onCapture: (imageUrls: string[]) => void;
  onBack: () => void;
}

const MAX_PHOTOS = 10;

export default function MultiPhotoCameraCapture({ onCapture, onBack }: MultiPhotoCameraCaptureProps) {
  const { toast } = useToast();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [capturedImages, setCapturedImages] = useState<string[]>([]);
  const [detectedEdges, setDetectedEdges] = useState<{ left: number | null; right: number | null }>({ left: null, right: null });
  const [edgeOpacity, setEdgeOpacity] = useState({ left: 0, right: 0 });
  const detectionCanvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const [alignmentStatus, setAlignmentStatus] = useState<'Aligned' | 'Adjust left' | 'Adjust right' | 'Move closer' | 'Move back' | null>(null);
  const [isAligned, setIsAligned] = useState(false);
  const [autoCapturProgress, setAutoCapturProgress] = useState(0);
  const [showFlash, setShowFlash] = useState(false);
  const alignmentTimerRef = useRef<NodeJS.Timeout>();
  const previousAlignmentRef = useRef<boolean>(false);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (alignmentTimerRef.current) {
        clearInterval(alignmentTimerRef.current);
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Camera access error:", error);
      }
      toast({
        title: "Camera Access Denied",
        description: "Please allow camera access to take photos",
        variant: "destructive",
      });
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  };

  // Edge detection algorithm - detects vertical edges in the frame
  const detectReceiptEdges = useCallback(() => {
    if (!videoRef.current || !detectionCanvasRef.current) return;

    const video = videoRef.current;
    const canvas = detectionCanvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    if (!ctx || video.readyState !== video.HAVE_ENOUGH_DATA) return;

    // Use small canvas for performance (scale down)
    const scale = 0.25;
    canvas.width = video.videoWidth * scale;
    canvas.height = video.videoHeight * scale;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Convert to grayscale and find vertical edges
    const width = canvas.width;
    const height = canvas.height;
    const edgeStrength: number[] = new Array(width).fill(0);

    // Sample middle section of frame (where receipt usually is)
    const startY = Math.floor(height * 0.2);
    const endY = Math.floor(height * 0.8);

    for (let x = 1; x < width - 1; x++) {
      let totalGradient = 0;
      let samples = 0;

      for (let y = startY; y < endY; y += 3) {
        const idx = (y * width + x) * 4;
        const leftIdx = (y * width + (x - 1)) * 4;
        const rightIdx = (y * width + (x + 1)) * 4;

        // Calculate grayscale
        const center = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
        const left = (data[leftIdx] + data[leftIdx + 1] + data[leftIdx + 2]) / 3;
        const right = (data[rightIdx] + data[rightIdx + 1] + data[rightIdx + 2]) / 3;

        // Horizontal gradient (Sobel-like)
        const gradient = Math.abs(right - left);
        totalGradient += gradient;
        samples++;
      }

      edgeStrength[x] = samples > 0 ? totalGradient / samples : 0;
    }

    // Find strongest edges in left and right halves
    const midPoint = Math.floor(width / 2);
    const threshold = 15; // Minimum edge strength
    
    let leftEdge: number | null = null;
    let rightEdge: number | null = null;
    let maxLeftStrength = threshold;
    let maxRightStrength = threshold;

    // Find left edge (search left quarter to middle)
    for (let x = Math.floor(width * 0.15); x < midPoint - 20; x++) {
      if (edgeStrength[x] > maxLeftStrength) {
        maxLeftStrength = edgeStrength[x];
        leftEdge = x;
      }
    }

    // Find right edge (search middle to right quarter)
    for (let x = midPoint + 20; x < Math.floor(width * 0.85); x++) {
      if (edgeStrength[x] > maxRightStrength) {
        maxRightStrength = edgeStrength[x];
        rightEdge = x;
      }
    }

    // Convert back to full video coordinates
    const videoWidth = video.videoWidth;
    const newEdges = {
      left: leftEdge !== null ? (leftEdge / scale) / videoWidth : null,
      right: rightEdge !== null ? (rightEdge / scale) / videoWidth : null,
    };

    setDetectedEdges(newEdges);

    // Smooth fade in/out for edge opacity
    setEdgeOpacity(prev => ({
      left: newEdges.left !== null 
        ? Math.min(prev.left + 0.1, 0.7) 
        : Math.max(prev.left - 0.1, 0),
      right: newEdges.right !== null 
        ? Math.min(prev.right + 0.1, 0.7) 
        : Math.max(prev.right - 0.1, 0),
    }));

    // Calculate alignment status
    if (newEdges.left !== null && newEdges.right !== null) {
      const leftPos = newEdges.left;
      const rightPos = newEdges.right;
      const width = rightPos - leftPos;
      const center = (leftPos + rightPos) / 2;
      
      // Check alignment conditions
      if (width < 0.3) {
        setAlignmentStatus('Move back');
        setIsAligned(false);
      } else if (width > 0.8) {
        setAlignmentStatus('Move closer');
        setIsAligned(false);
      } else if (center < 0.45) {
        setAlignmentStatus('Adjust right');
        setIsAligned(false);
      } else if (center > 0.55) {
        setAlignmentStatus('Adjust left');
        setIsAligned(false);
      } else {
        setAlignmentStatus('Aligned');
        setIsAligned(true);
      }
    } else {
      setAlignmentStatus(null);
      setIsAligned(false);
    }

    // Continue detection loop at ~30fps
    animationFrameRef.current = requestAnimationFrame(detectReceiptEdges);
  }, []);

  // Start edge detection when camera is ready
  useEffect(() => {
    if (stream && videoRef.current) {
      const video = videoRef.current;
      
      const startDetection = () => {
        if (video.readyState >= video.HAVE_ENOUGH_DATA) {
          detectReceiptEdges();
        }
      };

      video.addEventListener('loadeddata', startDetection);
      
      // Start immediately if already loaded
      if (video.readyState >= video.HAVE_ENOUGH_DATA) {
        startDetection();
      }

      return () => {
        video.removeEventListener('loadeddata', startDetection);
      };
    }
  }, [stream, detectReceiptEdges]);

  // Haptic feedback and auto-capture logic
  useEffect(() => {
    if (isAligned && !previousAlignmentRef.current) {
      // Haptic feedback when becoming aligned
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }
    }
    previousAlignmentRef.current = isAligned;

    // Auto-capture timer
    if (isAligned) {
      let progress = 0;
      const interval = 25; // Update every 25ms
      const totalTime = 2500; // 2.5 seconds
      const increment = (interval / totalTime) * 100;

      if (alignmentTimerRef.current) {
        clearInterval(alignmentTimerRef.current);
      }

      alignmentTimerRef.current = setInterval(() => {
        progress += increment;
        setAutoCapturProgress(progress);

        if (progress >= 100) {
          clearInterval(alignmentTimerRef.current!);
          capturePhoto();
          setAutoCapturProgress(0);
        }
      }, interval);
    } else {
      if (alignmentTimerRef.current) {
        clearInterval(alignmentTimerRef.current);
      }
      setAutoCapturProgress(0);
    }

    return () => {
      if (alignmentTimerRef.current) {
        clearInterval(alignmentTimerRef.current);
      }
    };
  }, [isAligned]);

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    if (capturedImages.length >= MAX_PHOTOS) {
      toast({
        title: "Maximum Photos Reached",
        description: `You can capture up to ${MAX_PHOTOS} photos per receipt`,
        variant: "destructive",
      });
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Flash effect
    if (flashEnabled) {
      const flashDiv = document.createElement('div');
      flashDiv.style.position = 'fixed';
      flashDiv.style.top = '0';
      flashDiv.style.left = '0';
      flashDiv.style.width = '100%';
      flashDiv.style.height = '100%';
      flashDiv.style.backgroundColor = 'white';
      flashDiv.style.zIndex = '9999';
      document.body.appendChild(flashDiv);
      setTimeout(() => document.body.removeChild(flashDiv), 100);
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Flash animation
    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 150);

    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        setCapturedImages(prev => [...prev, url]);
        toast({
          title: "Photo Captured!",
          description: `${capturedImages.length + 1}/${MAX_PHOTOS} photos`,
        });
      }
    }, 'image/jpeg', 0.9);
  };

  const handleConfirm = () => {
    if (capturedImages.length === 0) {
      toast({
        title: "No Photos Taken",
        description: "Please take at least one photo",
        variant: "destructive",
      });
      return;
    }
    stopCamera();
    onCapture(capturedImages);
  };

  return (
    <div className="fixed inset-0 bg-black flex flex-col">
      {/* Top thumbnail strip - Instacart-style minimal spacing */}
      {capturedImages.length > 0 && (
        <div className="absolute top-0 left-0 right-0 z-20 safe-area-top">
          <div className="flex gap-1 p-1.5 overflow-x-auto">
            {capturedImages.map((url, index) => (
              <div key={index} className="flex-shrink-0">
                <img 
                  src={url} 
                  alt={`Captured ${index + 1}`}
                  className="h-14 w-14 object-cover rounded-sm"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Camera preview with overlay guides */}
      <div className="flex-1 relative">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          className="w-full h-full object-cover"
        />
        <canvas ref={canvasRef} className="hidden" />
        <canvas ref={detectionCanvasRef} className="hidden" />
        
        {/* Vertical guide lines and labels - positioned at edges */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Instacart-style left edge gradient - 50% opacity at edge fading to 0% */}
          <div 
            className="absolute top-0 bottom-0 left-0"
            style={{ 
              width: '14%',
              background: 'linear-gradient(to right, rgba(0,0,0,0.5), rgba(0,0,0,0))',
            }}
          />

          {/* Instacart-style right edge gradient - 50% opacity at edge fading to 0% */}
          <div 
            className="absolute top-0 bottom-0 right-0"
            style={{ 
              width: '14%',
              background: 'linear-gradient(to left, rgba(0,0,0,0.5), rgba(0,0,0,0))',
            }}
          />

          {/* Left guide - Instacart style */}
          <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center">
            <div className="flex items-center gap-1">
              <div className="w-px h-32 bg-white/60" />
              <div 
                className="text-white text-[11px] font-normal tracking-wide opacity-65"
                style={{ 
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed'
                }}
              >
                Left receipt edge
              </div>
            </div>
          </div>
          
          {/* Right guide - Instacart style */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
            <div className="flex items-center gap-1">
              <div 
                className="text-white text-[11px] font-normal tracking-wide opacity-65"
                style={{ 
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed'
                }}
              >
                Right receipt edge
              </div>
              <div className="w-px h-32 bg-white/60" />
            </div>
          </div>
        </div>

        {/* Flash button - Instacart style: upper half, vertically centered, far right */}
        <div className="absolute right-5 top-1/4 -translate-y-1/2 z-10">
          <Button
            onClick={() => setFlashEnabled(!flashEnabled)}
            variant="ghost"
            size="icon"
            className="h-11 w-11 rounded-full bg-gray-600/55 hover:bg-gray-600/65 text-white border-0"
          >
            {flashEnabled ? <Zap className="h-5 w-5" /> : <ZapOff className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Flash overlay */}
      {showFlash && (
        <div className="absolute inset-0 bg-white z-30 animate-fade-out pointer-events-none" />
      )}

      {/* Bottom controls - clean minimal layout */}
      <div className="absolute bottom-0 left-0 right-0 z-10 pb-10 safe-area-bottom">
        {/* Alignment status text */}
        {alignmentStatus && (
          <div className="flex justify-center mb-3">
            <div 
              className={`px-4 py-1.5 rounded-full backdrop-blur-sm text-sm font-medium transition-all duration-300 ${
                isAligned 
                  ? 'bg-green-500/20 text-green-100' 
                  : 'bg-white/20 text-white'
              }`}
            >
              {alignmentStatus}
            </div>
          </div>
        )}

        <div className="flex items-center justify-center px-8 relative h-24">
          {/* Cancel X button - left */}
          <Button
            onClick={onBack}
            variant="ghost"
            size="icon"
            className="absolute left-8 h-10 w-10 rounded-full bg-transparent hover:bg-white/10 text-white p-0"
          >
            <X className="h-8 w-8 stroke-[2.5]" />
          </Button>

          {/* Capture button - center (large circular) with auto-capture ring */}
          <div className="relative">
            {/* Auto-capture progress ring */}
            {autoCapturProgress > 0 && (
              <svg 
                className="absolute inset-0 w-[72px] h-[72px] -rotate-90"
                style={{ 
                  transform: 'rotate(-90deg)',
                  top: '50%',
                  left: '50%',
                  marginLeft: '-36px',
                  marginTop: '-36px',
                }}
              >
                <circle
                  cx="36"
                  cy="36"
                  r="34"
                  stroke="rgba(34, 197, 94, 0.8)"
                  strokeWidth="3"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 34}`}
                  strokeDashoffset={`${2 * Math.PI * 34 * (1 - autoCapturProgress / 100)}`}
                  className="transition-all duration-100"
                />
              </svg>
            )}
            
            <Button
              onClick={capturePhoto}
              size="icon"
              className="h-[72px] w-[72px] rounded-full bg-white hover:bg-white/95 shadow-xl p-0 relative active:scale-95 transition-transform duration-150 border-[2.5px] border-white"
            >
              <div className="absolute inset-2 rounded-full border-[3px] border-black" />
            </Button>
          </div>

          {/* Confirm checkmark button - right */}
          {capturedImages.length > 0 && (
            <Button
              onClick={handleConfirm}
              variant="ghost"
              size="icon"
              className="absolute right-8 h-10 w-10 rounded-full bg-white/90 hover:bg-white text-black p-0"
            >
              <Check className="h-6 w-6 stroke-[2.5]" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
